(() => {
/**
 * Behaviour for the bfc-* landing sections.
 *
 * Three small custom elements rather than one script that scans the document,
 * so sections keep working when the theme editor adds, removes or re-renders a
 * single section without a page reload.
 */

/**
 * Countdown that supports both a fixed deadline and an evergreen per-visitor
 * timer.
 *
 * The evergreen mode is the one these offer pages actually use: each visitor
 * gets their own window, persisted in localStorage so a refresh does not hand
 * them a fresh hour. A shared fixed deadline that has already passed would
 * render a dead 00:00:00, so when the stored window expires we restart it
 * rather than freezing.
 */
class BfcCountdown extends HTMLElement {
  connectedCallback() {
    this.units = {
      hours: this.querySelector('[data-unit="hours"]'),
      minutes: this.querySelector('[data-unit="minutes"]'),
      seconds: this.querySelector('[data-unit="seconds"]'),
    };

    this.deadline = this.resolveDeadline();
    this.tick();
    this.timer = setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  resolveDeadline() {
    const fixed = this.getAttribute('end');

    if (fixed) {
      const parsed = Date.parse(fixed);
      if (!Number.isNaN(parsed) && parsed > Date.now()) return parsed;
    }

    const minutes = Number(this.getAttribute('minutes')) || 60;
    const key = this.getAttribute('storage-key') || 'bfc-countdown';
    const windowMs = minutes * 60 * 1000;

    let stored = null;
    try {
      stored = Number(window.localStorage.getItem(key));
    } catch {
      // Private mode / storage disabled. Fall through to a session-only window.
    }

    if (stored && stored > Date.now()) return stored;

    const next = Date.now() + windowMs;
    try {
      window.localStorage.setItem(key, String(next));
    } catch {
      // Non-fatal: the timer still runs, it just resets on reload.
    }
    return next;
  }

  tick() {
    let remaining = Math.max(0, this.deadline - Date.now());

    // Evergreen mode restarts instead of sitting at zero.
    if (remaining === 0 && !this.getAttribute('end')) {
      this.deadline = this.resolveDeadline();
      remaining = Math.max(0, this.deadline - Date.now());
    }

    const total = Math.floor(remaining / 1000);
    const pad = (n) => String(n).padStart(2, '0');

    if (this.units.hours) this.units.hours.textContent = pad(Math.floor(total / 3600));
    if (this.units.minutes) this.units.minutes.textContent = pad(Math.floor((total % 3600) / 60));
    if (this.units.seconds) this.units.seconds.textContent = pad(total % 60);
  }
}

/** Plus/minus stepper wrapping a native number input, so the form still posts normally. */
class BfcQuantity extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    if (!this.input) return;

    this.addEventListener('click', (event) => {
      const button = event.target.closest('button[data-step]');
      if (!button) return;

      event.preventDefault();

      const min = Number(this.input.min) || 1;
      const max = Number(this.input.max) || Infinity;
      const next = Number(this.input.value) + Number(button.dataset.step);

      this.input.value = String(Math.min(max, Math.max(min, next)));
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }
}

/** Thumbnail strip that swaps the main product image. */
class BfcGallery extends HTMLElement {
  connectedCallback() {
    // Selected by position rather than a data attribute on the <img>: image_tag
    // has no pass-through for arbitrary attributes, so anything we asked it to
    // add would render as a literal attribute name instead.
    this.main = this.querySelector('.bfc-offer__main img');
    if (!this.main) return;

    this.addEventListener('click', (event) => {
      const thumb = event.target.closest('button[data-src]');
      if (!thumb) return;

      this.main.src = thumb.dataset.src;
      if (thumb.dataset.srcset) this.main.srcset = thumb.dataset.srcset;
      this.main.alt = thumb.dataset.alt || '';

      this.querySelectorAll('button[data-src]').forEach((button) => {
        button.setAttribute('aria-current', String(button === thumb));
      });
    });
  }
}

if (!customElements.get('bfc-countdown')) customElements.define('bfc-countdown', BfcCountdown);
if (!customElements.get('bfc-quantity')) customElements.define('bfc-quantity', BfcQuantity);
if (!customElements.get('bfc-gallery')) customElements.define('bfc-gallery', BfcGallery);

/**
 * EasySell is injected after first paint and currently emits a low-contrast CTA,
 * an unlabelled product image, and customer fields without autocomplete hints.
 * Apply the small accessibility fixes once its generated form is present.
 */
if (!window.__bfcEasySellA11y) {
  window.__bfcEasySellA11y = true;

  const improveEasySell = () => {
    const root = document.querySelector('#easysell');
    if (!root) return false;

    const button = root.querySelector('#es-form-button');
    button?.style.setProperty('background-color', '#137333', 'important');

    const image = root.querySelector('img.es-image');
    if (image && !image.hasAttribute('alt')) {
      const productName = root.querySelector('a[href*="/products/"]')?.textContent.trim()
        || document.querySelector('.bfc-title')?.textContent.trim()
        || '';
      image.alt = productName;
    }

    const name = root.querySelector('input[name="first_name"]');
    const phone = root.querySelector('input[type="tel"], input[name="phone"]');
    if (name) name.autocomplete = 'name';
    if (phone) phone.autocomplete = 'tel';

    return Boolean(button && image && name && phone);
  };

  if (!improveEasySell()) {
    const observer = new MutationObserver(() => {
      if (improveEasySell()) observer.disconnect();
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
    window.setTimeout(() => observer.disconnect(), 10000);
  }
}
})();
